import { ruleItemInjector } from "../Quadrant/quadrantHelper";
import { BULK_SCOPE, getRowBulkId } from "./bulkSelectionModes";

function ruleItem(member, operator, values) {
  return {
    type: "RULE_ITEM",
    member,
    operator,
    values: (values || []).map(String),
  };
}

function andRule(items = []) {
  return { type: "AND_RULE", and: items };
}

function orRule(items = []) {
  return { type: "OR_RULE", or: items };
}

/** Build AND_RULE of equals for one row's composite (or single) keys. */
function buildRowMatchAndRule(row, idKeys = []) {
  const items = (idKeys || [])
    .map((key) => {
      const value = row?.[key];
      if (value === undefined || value === null || value === "") return null;
      return ruleItem(key, "equals", [value]);
    })
    .filter(Boolean);
  if (!items.length) return null;
  return items.length === 1 ? items[0] : andRule(items);
}

/**
 * De Morgan exclude for one composite row:
 * NOT (a AND b AND c) => (a notEquals) OR (b notEquals) OR (c notEquals)
 */
function buildRowExcludeOrRule(row, idKeys = []) {
  const items = (idKeys || [])
    .map((key) => {
      const value = row?.[key];
      if (value === undefined || value === null || value === "") return null;
      return ruleItem(key, "notEquals", [value]);
    })
    .filter(Boolean);
  if (!items.length) return null;
  return items.length === 1 ? items[0] : orRule(items);
}

function resolveExcludeRows({ excludeRows, excludeIds, rows, idKeys }) {
  if (excludeRows?.length) return excludeRows;
  if (!excludeIds?.length || !rows?.length) return [];
  const excludeSet = new Set(excludeIds.map(String));
  return rows.filter((row) => excludeSet.has(getRowBulkId(row, idKeys)));
}

function resolveSelectedRows({ selectedRows, selectedIds, rows, idKeys }) {
  if (selectedRows?.length) return selectedRows;
  if (!selectedIds?.length || !rows?.length) return [];
  const selectedSet = new Set(selectedIds.map(String));
  return rows.filter((row) => selectedSet.has(getRowBulkId(row, idKeys)));
}

/**
 * Inject selection/exclusion into base filters as ComplexFilter AND/OR rules.
 *
 * Single key:
 *  - selected  → equals [all values]  (IN)
 *  - except    → notEquals [all values] (NOT IN)
 *
 * Composite keys:
 *  - selected  → OR( AND(equals…), AND(equals…) )
 *  - except    → AND( OR(notEquals…), OR(notEquals…) )  // De Morgan per row
 */
export function buildBulkQuery({
  scope = BULK_SCOPE.SELECTED,
  baseFilters = {},
  selectedRows = [],
  excludeRows = [],
  selectedIds = [],
  excludeIds = [],
  rows = [],
  idKeys = [],
  idFilterMember,
  queryContext = {},
}) {
  const keys =
    idKeys?.length > 0
      ? idKeys
      : idFilterMember
        ? [idFilterMember]
        : [];
  const isComposite = keys.length > 1;
  const singleMember = !isComposite ? keys[0] || idFilterMember : null;

  let filters = JSON.parse(JSON.stringify(baseFilters || {}));

  const selected = resolveSelectedRows({
    selectedRows,
    selectedIds,
    rows,
    idKeys: keys,
  });
  const excluded = resolveExcludeRows({
    excludeRows,
    excludeIds,
    rows,
    idKeys: keys,
  });

  if (scope === BULK_SCOPE.SELECTED) {
    if (!selected.length || !keys.length) {
      return {
        scope,
        query: { ...queryContext, filters },
      };
    }

    if (!isComposite && singleMember) {
      const values = selected
        .map((row) => row?.[singleMember])
        .filter((v) => v !== undefined && v !== null && v !== "")
        .map(String);
      if (values.length) {
        filters = ruleItemInjector(
          filters,
          "dimensions",
          [ruleItem(singleMember, "equals", values)],
          "AND_RULE"
        );
      }
    } else {
      const rowRules = selected
        .map((row) => buildRowMatchAndRule(row, keys))
        .filter(Boolean);
      if (rowRules.length === 1) {
        filters = ruleItemInjector(filters, "dimensions", [rowRules[0]], "AND_RULE");
      } else if (rowRules.length > 1) {
        filters = ruleItemInjector(
          filters,
          "dimensions",
          [orRule(rowRules)],
          "AND_RULE"
        );
      }
    }

    return {
      scope,
      query: { ...queryContext, filters },
    };
  }

  if (scope === BULK_SCOPE.ALL_MATCHING) {
    return {
      scope,
      query: { ...queryContext, filters },
    };
  }

  // ALL_MATCHING_EXCEPT
  if (!excluded.length || !keys.length) {
    return {
      scope,
      query: { ...queryContext, filters },
    };
  }

  if (!isComposite && singleMember) {
    const values = excluded
      .map((row) => row?.[singleMember])
      .filter((v) => v !== undefined && v !== null && v !== "")
      .map(String);
    if (values.length) {
      filters = ruleItemInjector(
        filters,
        "dimensions",
        [ruleItem(singleMember, "notEquals", values)],
        "AND_RULE"
      );
    }
  } else {
    const excludeRules = excluded
      .map((row) => buildRowExcludeOrRule(row, keys))
      .filter(Boolean);
    if (excludeRules.length) {
      // Each excluded composite is AND'd with existing filters.
      filters = ruleItemInjector(
        filters,
        "dimensions",
        excludeRules,
        "AND_RULE"
      );
    }
  }

  return {
    scope,
    query: { ...queryContext, filters },
  };
}
